(function () {
  const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];

  function clamp(number, min, max) {
    return Math.max(min, Math.min(max, number));
  }

  function confidenceFromDistance(distance) {
    const cleanDistance = clamp(Number(distance) || 0, 0, 0.5);
    const confidence = Math.round(clamp(94 - cleanDistance * 18, 84, 96));
    const secondConfidence = Math.round(clamp(76 + cleanDistance * 14, 68, confidence - 7));

    return { confidence, secondConfidence };
  }

  function confidenceFromRangeScore(score) {
    const cleanScore = Math.max(0, Number(score) || 0);
    const confidence = Math.round(clamp(94 - cleanScore * 10, 78, 95));
    const gap = Math.round(clamp(10 + cleanScore * 4, 10, 24));
    const secondConfidence = Math.round(clamp(confidence - gap, 62, confidence - 7));

    return { confidence, secondConfidence };
  }

  function normalizeSize(value) {
    return String(value || "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace("XXXL", "3XL")
      .replace("3XL+", "3XL");
  }

  function variantSize(variant) {
    const values = [];

    if (variant.title) values.push(variant.title);
    if (Array.isArray(variant.options)) values.push(...variant.options);

    const joined = values.map(normalizeSize).join("|");

    if (/(^|\|)3XL(\+)?($|\|)/.test(joined)) return "3XL";
    if (/(^|\|)XXL($|\|)/.test(joined)) return "XXL";
    if (/(^|\|)XL($|\|)/.test(joined)) return "XL";
    if (/(^|\|)L($|\|)/.test(joined)) return "L";
    if (/(^|\|)M($|\|)/.test(joined)) return "M";
    if (/(^|\|)S($|\|)/.test(joined)) return "S";
    if (/(^|\|)XS($|\|)/.test(joined)) return "XS";

    return "";
  }

  const fitProfiles = {
    shirt: {
      label: "Football Shirt",
      note: "Answer a few quick questions and we will recommend a football shirt size.",
      subtitle: "The calculation is based on your height, weight and fit preference. This recommendation is for football shirts only.",
      resultCopy: "in a football shirt"
    },
    jacket: {
      label: "Jacket",
      note: "Answer a few quick questions and we will recommend a jacket size.",
      subtitle: "The calculation is based on your height, weight and fit preference, using the jacket size chart.",
      resultCopy: "in a jacket"
    }
  };

  const jacketRanges = [
    { size: "S", height: [165, 170], weight: [45, 60] },
    { size: "M", height: [170, 175], weight: [60, 68] },
    { size: "L", height: [170, 175], weight: [68, 75] },
    { size: "XL", height: [175, 180], weight: [75, 82] },
    { size: "XXL", height: [180, 185], weight: [82, 95] }
  ];

  function distanceToRange(value, range) {
    if (value >= range[0] && value <= range[1]) return 0;
    if (value < range[0]) return range[0] - value;
    return value - range[1];
  }

  function estimateByRanges(height, weight, age, fitPreference, ranges) {
    let best = ranges[0];
    let bestScore = Infinity;

    ranges.forEach((range) => {
      const heightScore = distanceToRange(height, range.height) / 4;
      const weightScore = distanceToRange(weight, range.weight) / 6;
      const middleWeight = (range.weight[0] + range.weight[1]) / 2;
      const tieBreaker = Math.abs(weight - middleWeight) / 60;
      const score = heightScore + weightScore + tieBreaker;

      if (score < bestScore) {
        bestScore = score;
        best = range;
      }
    });

    let index = sizeOrder.indexOf(best.size);

    if (fitPreference === "tight") index -= 1;
    if (fitPreference === "loose") index += 1;
    if (age >= 55 && fitPreference !== "tight") index += 0;

    index = clamp(index, 0, sizeOrder.length - 1);

    let secondIndex = fitPreference === "tight" ? index + 1 : index - 1;
    if (secondIndex < 0 || secondIndex >= sizeOrder.length || secondIndex === index) {
      secondIndex = clamp(index + 1, 0, sizeOrder.length - 1);
    }

    const confidencePair = confidenceFromRangeScore(bestScore);

    return {
      size: sizeOrder[index],
      confidence: confidencePair.confidence,
      secondSize: sizeOrder[secondIndex],
      secondConfidence: confidencePair.secondConfidence
    };
  }

  function estimateSize(height, weight, age, fitPreference, fitType) {
    if (fitType === "jacket") {
      return estimateByRanges(height, weight, age, fitPreference, jacketRanges);
    }

    let score = (weight - 52) / 11 + (height - 168) / 14;

    if (age >= 45) score += 0.12;
    if (age >= 60) score += 0.12;

    if (fitPreference === "tight") score -= 0.42;
    if (fitPreference === "loose") score += 0.48;

    const index = clamp(Math.round(score), 0, sizeOrder.length - 1);
    const exact = clamp(score, 0, sizeOrder.length - 1);
    const distance = Math.abs(exact - index);
    const confidencePair = confidenceFromDistance(distance);

    let secondIndex = exact > index ? index + 1 : index - 1;
    secondIndex = clamp(secondIndex, 0, sizeOrder.length - 1);

    if (secondIndex === index) {
      secondIndex = clamp(index + 1, 0, sizeOrder.length - 1);
    }

    return {
      size: sizeOrder[index],
      confidence: confidencePair.confidence,
      secondSize: sizeOrder[secondIndex],
      secondConfidence: confidencePair.secondConfidence
    };
  }

  function nearestAvailableSize(size, productData) {
    const availableSizes = Array.from(new Set((productData.variants || [])
      .filter((variant) => variant.available)
      .map(variantSize)
      .filter(Boolean)));

    if (!availableSizes.length) {
      return { size, exact: false, variant: null };
    }

    if (availableSizes.includes(size)) {
      return {
        size,
        exact: true,
        variant: findVariantBySize(size, productData)
      };
    }

    const wantedIndex = sizeOrder.indexOf(size);
    let best = availableSizes[0];
    let bestDistance = 99;

    availableSizes.forEach((availableSize) => {
      const distance = Math.abs(sizeOrder.indexOf(availableSize) - wantedIndex);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = availableSize;
      }
    });

    return {
      size: best,
      exact: false,
      variant: findVariantBySize(best, productData)
    };
  }

  function findVariantBySize(size, productData) {
    return (productData.variants || []).find((variant) => {
      return variant.available && variantSize(variant) === size;
    }) || null;
  }

  let sizeGroupMapPromise = null;
  const mappedProductCache = new Map();

  function baseHandleFromCurrent(handle) {
    return String(handle || "").replace(/-size-[^-]+$/i, "");
  }

  function loadSizeGroupMap(url) {
    if (!url) return Promise.resolve(null);
    if (!sizeGroupMapPromise) {
      sizeGroupMapPromise = fetch(url, { credentials: "same-origin", cache: "force-cache" })
        .then((response) => {
          if (!response.ok) throw new Error("Size group map request failed");
          return response.json();
        })
        .catch(() => null);
    }
    return sizeGroupMapPromise;
  }

  function fetchMappedProduct(handle) {
    const key = String(handle || "").trim();
    if (!key) return Promise.resolve(null);
    if (mappedProductCache.has(key)) return mappedProductCache.get(key);

    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
    const promise = fetch(`${root}products/${encodeURIComponent(key)}.js`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" }
    })
      .then((response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .catch(() => null);

    mappedProductCache.set(key, promise);
    return promise;
  }

  async function nearestMappedAvailableSize(size, productData) {
    const map = await loadSizeGroupMap(productData && productData.sizeMapUrl);
    if (!map) return null;

    const baseHandle = baseHandleFromCurrent(productData && (productData.currentHandle || productData.handle));
    const entries = Array.isArray(map[baseHandle]) ? map[baseHandle] : [];
    if (!entries.length) return null;

    const wantedIndex = sizeOrder.indexOf(normalizeSize(size));
    const candidates = entries
      .map((entry) => ({ size: normalizeSize(entry && entry[0]), handle: String(entry && entry[1] || "") }))
      .filter((entry) => entry.size && entry.handle && sizeOrder.includes(entry.size))
      .sort((a, b) => {
        const aDistance = Math.abs(sizeOrder.indexOf(a.size) - wantedIndex);
        const bDistance = Math.abs(sizeOrder.indexOf(b.size) - wantedIndex);
        return aDistance - bDistance;
      });

    const checks = await Promise.all(candidates.map(async (candidate) => {
      const product = await fetchMappedProduct(candidate.handle);
      const variant = product && Array.isArray(product.variants)
        ? product.variants.find((item) => item && item.available)
        : null;
      return variant ? { ...candidate, variantId: variant.id, exact: candidate.size === normalizeSize(size) } : null;
    }));

    return checks.find(Boolean) || null;
  }

  function setStep(root, stepNumber) {
    root.querySelectorAll("[data-regista-fit-step]").forEach((step) => {
      step.classList.toggle("is-active", step.getAttribute("data-regista-fit-step") === String(stepNumber));
    });
  }

  function getProductData(root) {
    const script = root.querySelector("[data-regista-fit-product]");
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function renderBars(root, result) {
    const bars = root.querySelector("[data-regista-fit-bars]");
    if (!bars) return;

    bars.innerHTML = `
      <div class="regista-fit-bar">
        <div class="regista-fit-bar__head"><span>${result.size}</span><span>${result.confidence}%</span></div>
        <div class="regista-fit-bar__track"><div class="regista-fit-bar__fill" style="width:${result.confidence}%"></div></div>
      </div>
      <div class="regista-fit-bar regista-fit-bar--secondary">
        <div class="regista-fit-bar__head"><span>${result.secondSize}</span><span>${result.secondConfidence}%</span></div>
        <div class="regista-fit-bar__track"><div class="regista-fit-bar__fill" style="width:${result.secondConfidence}%"></div></div>
      </div>
    `;
  }

  function selectVariant(productData, variantId) {
    if (!productData || !variantId) return false;

    const form = document.getElementById(productData.formId);
    const picker = document.querySelector(`variant-picker[form="${CSS.escape(productData.formId)}"]`);

    if (picker && typeof picker.selectVariant === "function") {
      picker.selectVariant(Number(variantId));
      return true;
    }

    const masterInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
    if (masterInput) {
      masterInput.disabled = false;
      masterInput.value = String(variantId);
      masterInput.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    return false;
  }

  function initModal(modal) {
    if (modal.dataset.registaFitReady === "true") return;
    modal.dataset.registaFitReady = "true";

    const productData = getProductData(modal);
    const fitType = productData && productData.fitType ? productData.fitType : "shirt";
    const profile = fitProfiles[fitType] || fitProfiles.shirt;

    const state = {
      units: "",
      height: 0,
      weight: 0,
      age: 0,
      fit: "regular",
      finalSize: "",
      finalVariantId: null,
      finalProductHandle: ""
    };

    const heightMetricInput = modal.querySelector("[data-regista-fit-height-metric]");
    const weightMetricInput = modal.querySelector("[data-regista-fit-weight-metric]");
    const heightFeetInput = modal.querySelector("[data-regista-fit-height-feet]");
    const heightInchesInput = modal.querySelector("[data-regista-fit-height-inches]");
    const weightImperialInput = modal.querySelector("[data-regista-fit-weight-imperial]");
    const ageInput = modal.querySelector("[data-regista-fit-age]");
    const note = modal.querySelector("[data-regista-fit-note]");
    const subtitle = modal.querySelector("[data-regista-fit-subtitle]");
    const fitQuestion = modal.querySelector("[data-regista-fit-question]");
    const fitGuide = modal.querySelector("[data-regista-fit-guide]");

    modal.dataset.registaFitDefaultNote = profile.note;
    if (note) note.textContent = profile.note;
    if (subtitle) subtitle.textContent = profile.subtitle;
    if (fitQuestion) fitQuestion.textContent = `How do you like your ${profile.label.toLowerCase()} to fit?`;
    if (fitGuide) fitGuide.textContent = fitType === "jacket" ? "For jackets, choose a looser fit if you plan to wear a layer underneath or prefer more room." : "If you prefer more room in a Dri-FIT shirt, you will usually be more comfortable sizing up.";

    function imperialToMetric() {
      const feet = Number(heightFeetInput && heightFeetInput.value);
      const inches = Number(heightInchesInput && heightInchesInput.value);
      const pounds = Number(weightImperialInput && weightImperialInput.value);
      const totalInches = feet * 12 + inches;

      return {
        height: totalInches * 2.54,
        weight: pounds * 0.45359237
      };
    }

    function metricValues() {
      return {
        height: Number(heightMetricInput && heightMetricInput.value),
        weight: Number(weightMetricInput && weightMetricInput.value)
      };
    }

    function currentMeasurements() {
      return state.units === "imperial" ? imperialToMetric() : metricValues();
    }

    function syncMeasurementValues(nextUnit) {
      if (!state.units || state.units === nextUnit) return;

      if (state.units === "metric" && nextUnit === "imperial") {
        const metric = metricValues();
        if (metric.height > 0 && metric.weight > 0) {
          const totalInches = metric.height / 2.54;
          const feet = Math.floor(totalInches / 12);
          const inches = Math.round((totalInches - feet * 12) * 10) / 10;
          heightFeetInput.value = String(feet);
          heightInchesInput.value = String(inches);
          weightImperialInput.value = String(Math.round((metric.weight / 0.45359237) * 10) / 10);
        }
      } else if (state.units === "imperial" && nextUnit === "metric") {
        const metric = imperialToMetric();
        if (metric.height > 0 && metric.weight > 0) {
          heightMetricInput.value = String(Math.round(metric.height * 10) / 10);
          weightMetricInput.value = String(Math.round(metric.weight * 10) / 10);
        }
      }
    }

    function showMeasurementSet(unit) {
      modal.querySelectorAll("[data-regista-fit-measurements]").forEach((group) => {
        group.hidden = group.getAttribute("data-regista-fit-measurements") !== unit;
      });
    }

    function validateStepOne() {
      const values = currentMeasurements();
      const button = modal.querySelector('[data-regista-fit-step="1"] [data-regista-fit-next]');
      const imperialInputsValid = state.units !== "imperial" || (
        Number(heightFeetInput.value) >= 3 &&
        Number(heightFeetInput.value) <= 7 &&
        Number(heightInchesInput.value) >= 0 &&
        Number(heightInchesInput.value) < 12 &&
        Number(weightImperialInput.value) > 0
      );
      button.disabled = !(
        state.units &&
        imperialInputsValid &&
        values.height >= 120 &&
        values.height <= 230 &&
        values.weight >= 35 &&
        values.weight <= 180
      );
    }

    function validateStepTwo() {
      const age = Number(ageInput.value);
      const button = modal.querySelector('[data-regista-fit-step="2"] [data-regista-fit-next]');
      button.disabled = !(age >= 12 && age <= 100);
    }

    [heightMetricInput, weightMetricInput, heightFeetInput, heightInchesInput, weightImperialInput].forEach((input) => {
      if (input) input.addEventListener("input", validateStepOne);
    });
    ageInput.addEventListener("input", validateStepTwo);

    modal.querySelectorAll("[data-regista-fit-unit]").forEach((button) => {
      button.addEventListener("click", () => {
        const nextUnit = button.getAttribute("data-regista-fit-unit") === "imperial" ? "imperial" : "metric";
        syncMeasurementValues(nextUnit);
        state.units = nextUnit;
        showMeasurementSet(nextUnit);
        validateStepOne();
        if (note) {
          note.textContent = nextUnit === "imperial"
            ? "Enter your height in feet and inches and your weight in pounds."
            : "Enter your height in centimeters and your weight in kilograms.";
        }
        setStep(modal, 1);

        const firstInput = nextUnit === "imperial" ? heightFeetInput : heightMetricInput;
        if (firstInput) window.setTimeout(() => firstInput.focus(), 0);
      });
    });

    modal.querySelectorAll("[data-regista-fit-next]").forEach((button) => {
      button.addEventListener("click", () => {
        const currentStep = button.closest("[data-regista-fit-step]").getAttribute("data-regista-fit-step");

        if (currentStep === "1") {
          const measurements = currentMeasurements();
          state.height = measurements.height;
          state.weight = measurements.weight;
          if (note) note.textContent = "We have an initial estimate. Two more quick questions will help refine it.";
          setStep(modal, 2);
        } else if (currentStep === "2") {
          state.age = Number(ageInput.value);
          if (note) note.textContent = `Almost finished. Now choose how you like your ${profile.label.toLowerCase()} to fit.`;
          setStep(modal, 3);
        }
      });
    });

    modal.querySelectorAll("[data-regista-fit-back]").forEach((button) => {
      button.addEventListener("click", () => {
        const currentStep = Number(button.closest("[data-regista-fit-step]").getAttribute("data-regista-fit-step"));
        if (currentStep === 1 && note) note.textContent = profile.note;
        setStep(modal, Math.max(0, currentStep - 1));
      });
    });

    modal.querySelectorAll("[data-fit-pref]").forEach((button) => {
      button.addEventListener("click", () => {
        modal.querySelectorAll("[data-fit-pref]").forEach((item) => item.classList.remove("is-selected"));
        button.classList.add("is-selected");
        state.fit = button.getAttribute("data-fit-pref") || "regular";
      });
    });

    modal.querySelector("[data-regista-fit-calculate]").addEventListener("click", async () => {
      const calculateButton = modal.querySelector("[data-regista-fit-calculate]");
      const estimate = estimateSize(state.height, state.weight, state.age, state.fit, fitType);
      const nativeAvailable = nearestAvailableSize(estimate.size, productData || { variants: [] });

      if (calculateButton) {
        calculateButton.disabled = true;
        calculateButton.textContent = "Checking available sizes…";
      }

      let shownSize = estimate.size;
      let exact = false;
      state.finalVariantId = null;
      state.finalProductHandle = "";

      if (nativeAvailable && nativeAvailable.variant) {
        shownSize = nativeAvailable.size || estimate.size;
        exact = nativeAvailable.exact;
        state.finalVariantId = nativeAvailable.variant.id;
      } else {
        const mappedAvailable = await nearestMappedAvailableSize(estimate.size, productData || {});
        if (mappedAvailable) {
          shownSize = mappedAvailable.size || estimate.size;
          exact = mappedAvailable.exact;
          state.finalVariantId = mappedAvailable.variantId || null;
          state.finalProductHandle = mappedAvailable.handle || "";
        }
      }

      state.finalSize = shownSize;

      const title = modal.querySelector("[data-regista-fit-result-title]");
      const size = modal.querySelector("[data-regista-fit-size]");
      const copy = modal.querySelector("[data-regista-fit-result-copy]");
      const selectButton = modal.querySelector("[data-regista-fit-select-size]");
      const hasAvailableSize = Boolean(state.finalVariantId || state.finalProductHandle);

      if (hasAvailableSize) {
        title.textContent = exact ? "Your recommended size is available" : "Closest available size";
      } else {
        title.textContent = "Your recommended size";
      }
      size.textContent = shownSize;

      const finalResult = {
        size: shownSize,
        confidence: exact ? estimate.confidence : Math.max(76, estimate.confidence - 6),
        secondSize: estimate.secondSize === shownSize ? estimate.size : estimate.secondSize,
        secondConfidence: estimate.secondConfidence
      };

      renderBars(modal, finalResult);

      if (exact) {
        copy.innerHTML = `Based on your height, weight and fit preference, size <strong>${shownSize}</strong> is likely to be your most comfortable choice ${profile.resultCopy}.`;
      } else if (hasAvailableSize) {
        copy.innerHTML = `Your initial recommendation is <strong>${estimate.size}</strong>. The closest size currently available for this shirt is <strong>${shownSize}</strong>.`;
      } else {
        copy.innerHTML = `Based on your height, weight and fit preference, size <strong>${estimate.size}</strong> is likely to be your most comfortable choice ${profile.resultCopy}.`;
      }

      if (state.finalProductHandle) {
        selectButton.textContent = `View Size ${shownSize}`;
        selectButton.disabled = false;
      } else if (state.finalVariantId) {
        selectButton.textContent = `Select Size ${shownSize}`;
        selectButton.disabled = false;
      } else {
        selectButton.textContent = "No matching size currently available";
        selectButton.disabled = true;
      }

      if (calculateButton) {
        calculateButton.disabled = false;
        calculateButton.textContent = "Calculate My Size";
      }
      if (note) note.textContent = "Your recommendation stays in your browser only. We do not store this information.";
      setStep(modal, 4);
    });

    modal.querySelector("[data-regista-fit-select-size]").addEventListener("click", () => {
      if (state.finalProductHandle) {
        const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
        window.location.assign(`${root}products/${encodeURIComponent(state.finalProductHandle)}`);
        return;
      }

      if (selectVariant(productData, state.finalVariantId)) {
        closeModal(modal);
      }
    });

    modal.querySelector("[data-regista-fit-restart]").addEventListener("click", () => {
      if (note) note.textContent = profile.note;
      setStep(modal, 0);
    });
  }

  function openModal(modal) {
    modal.__registaPreviouslyFocused = document.activeElement;
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    initModal(modal);
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("regista-fit-modal-open");
    document.body.classList.add("regista-fit-modal-open");
    document.body.style.overflow = "hidden";
    setStep(modal, 0);
    const note = modal.querySelector("[data-regista-fit-note]");
    if (note && modal.dataset.registaFitDefaultNote) note.textContent = modal.dataset.registaFitDefaultNote;
    const closeButton = modal.querySelector("button[data-regista-fit-close]");
    if (closeButton) window.setTimeout(() => closeButton.focus(), 0);
  }

  function closeModal(modal) {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("regista-fit-modal-open");
    document.body.classList.remove("regista-fit-modal-open");
    document.body.style.overflow = "";
    if (modal.__registaPreviouslyFocused && typeof modal.__registaPreviouslyFocused.focus === "function") {
      modal.__registaPreviouslyFocused.focus();
    }
  }

  document.addEventListener("click", (event) => {
    const openButton = event.target.closest("[data-regista-fit-open]");
    if (openButton) {
      const container = openButton.closest(".product-info, .quick-buy, body");
      const modal = (container && container.querySelector("[data-regista-fit-modal]")) || document.querySelector("[data-regista-fit-modal]");
      if (modal) openModal(modal);
      return;
    }

    const closeButton = event.target.closest("[data-regista-fit-close]");
    if (closeButton) {
      const modal = closeButton.closest("[data-regista-fit-modal]");
      if (modal) closeModal(modal);
    }
  });

  document.addEventListener("keydown", (event) => {
    const modal = document.querySelector("[data-regista-fit-modal].is-open");
    if (!modal) return;
    if (event.key === "Escape") {
      closeModal(modal);
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(modal.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), select:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])'))
      .filter((item) => item.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const sectionId = event && event.detail ? String(event.detail.sectionId || "") : "";
    if (!sectionId) return;

    document.querySelectorAll("[data-regista-fit-modal]").forEach((modal) => {
      if (modal.getAttribute("data-regista-fit-owner") === sectionId) modal.remove();
    });

    document.documentElement.classList.remove("regista-fit-modal-open");
    document.body.classList.remove("regista-fit-modal-open");
    document.body.style.overflow = "";
  });
})();
